﻿import React, { Fragment, useState } from 'react';

import { FindSKUContent } from 'pages/sku_management/components/FindSKUContent';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { CreateAlterSKUPayload, SKUApi } from 'apis/SKUApi';
import { ContentDeliverable } from 'apis/TransactionApi';
import { FormattedMessage, useIntl } from 'react-intl';
import { Col, Container, Row } from 'react-bootstrap';
import { useNotification } from 'app/notifications';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

interface NewSKUFormProps {
    ActionsWrapper?: React.ElementType;
    ContentWrapper?: React.ElementType;
    onClose?: () => void;
    onSuccess?: (primaryExternalId: number, skuName: string) => void;
}

const SKU_NAME_REGEX = /^[A-Za-z0-9-]+$/;
const MAX_REDEMPTIONS = 999;

type InvalidState = {
    name: { message: string; validType: '' | 'invalid' | 'valid' };
    redemptions: { message: string; validType: '' | 'invalid' | 'valid' };
};

const NewSKUForm = ({ ActionsWrapper = Fragment, ContentWrapper = Fragment, onClose, onSuccess }: NewSKUFormProps) => {

    const { userId } = useApp();
    const { catchWithToast, createToast } = useNotification();
    const { formatMessage } = useIntl();

    const [invalidProps, setInvalidProps] = useState<InvalidState>({
        name: { message: '', validType: '' },
        redemptions: { message: '', validType: '' }
    });

    const [state, setState] = useSuperState({
        additionalIdentifier: '',
        numberOfRedemptions: 1 as number | '',
        saving: false,
        selectedContent: [] as ContentDeliverable[],
        skuName: '',
        step: 0 as 0 | 1,
        validating: false
    });

    const { additionalIdentifier, numberOfRedemptions, saving, selectedContent, skuName, step, validating } = state;

    const handleNameBlur = async () => {

        const value = skuName.trim();
        if (value === '') {

            setInvalidProps(prev => ({ ...prev, name: { message: formatMessage({ id: 'this_is_a_required_field.a7314606', defaultMessage: 'This is a required field.' }), validType: 'invalid' } }));
            return;

        }
        if (!SKU_NAME_REGEX.test(value)) {

            setInvalidProps(prev => ({
                ...prev,
                name: { message: formatMessage({ id: 'the_sku_name_may_only_contain_letters_nu.9636cb1e', defaultMessage: 'The SKU name may only contain letters, numbers, and hyphens.' }), validType: 'invalid' }
            }));
            return;

        }

        setState({ validating: true });
        try {

            const result = await SKUApi.ValidateSKU(value);
            if (result?.Done) {

                setInvalidProps(prev => ({ ...prev, name: { message: '', validType: 'valid' } }));

            } else {

                setInvalidProps(prev => ({
                    ...prev,
                    name: { message: result?.Message || formatMessage({ id: 'this_sku_name_is_already_in_use.bfe042d5', defaultMessage: 'This SKU name is already in use.' }), validType: 'invalid' }
                }));

            }

        } catch (e) {

            setInvalidProps(prev => ({ ...prev, name: { message: formatMessage({ id: 'unable_to_validate_sku_name.7fbd0a40', defaultMessage: 'Unable to validate SKU name.' }), validType: 'invalid' } }));

        } finally {

            setState({ validating: false });

        }

    };

    const validateForm = (): boolean => {

        const next: InvalidState = {
            name: { message: '', validType: '' },
            redemptions: { message: '', validType: '' }
        };

        const trimmedName = skuName.trim();
        if (trimmedName === '') {

            next.name = { message: formatMessage({ id: 'this_is_a_required_field.a7314606', defaultMessage: 'This is a required field.' }), validType: 'invalid' };

        } else if (!SKU_NAME_REGEX.test(trimmedName)) {

            next.name = { message: formatMessage({ id: 'the_sku_name_may_only_contain_letters_nu.9636cb1e', defaultMessage: 'The SKU name may only contain letters, numbers, and hyphens.' }), validType: 'invalid' };

        } else if (invalidProps.name.validType === 'invalid') {

            next.name = invalidProps.name;

        }

        if (!numberOfRedemptions || numberOfRedemptions < 1) {

            next.redemptions = { message: formatMessage({ id: 'please_enter_a_number_of_at_least_1.41d4fd6f', defaultMessage: 'Please enter a number of at least 1.' }), validType: 'invalid' };

        } else if (numberOfRedemptions > MAX_REDEMPTIONS) {

            next.redemptions = { message: `Number of redemptions may not exceed ${MAX_REDEMPTIONS}.`, validType: 'invalid' };

        }

        setInvalidProps(next);
        return next.name.validType !== 'invalid' && next.redemptions.validType !== 'invalid';

    };

    const handleCreate = async () => {

        if (!validateForm()) return;

        setState({ saving: true });

        try {

            const payload: CreateAlterSKUPayload = {
                AdditionalIdentifier: additionalIdentifier.trim(),
                Content: selectedContent.filter(c => !c.IsCustomTest),
                NumberOfRedemptions: Number(numberOfRedemptions),
                SKU: skuName.trim(),
                SkuId: 0,
                UserId: userId
            };
            const result = await SKUApi.CreateAlterSKU(payload);
            if (result?.Done) {

                const primaryExternalId = (result as any).Id ?? (result as any).ExtraData ?? 0;
                onSuccess?.(primaryExternalId, payload.SKU);

            } else {

                createToast(result?.Message || formatMessage({ id: 'failed_to_create_sku.d9e8e26f', defaultMessage: 'Failed to create SKU.' }), 'danger');

            }

        } catch (e) {

            catchWithToast(e, formatMessage({ id: 'failed_to_create_sku.d9e8e26f', defaultMessage: 'Failed to create SKU.' }));

        } finally {

            setState({ saving: false });

        }

    };

    if (step === 0) {

        return (
            <FindSKUContent
                onSuccess={contentData => {

                    if (contentData && Array.isArray(contentData.content)) {

                        const filtered = contentData.content.filter(c => !c.IsCustomTest);
                        setState({ selectedContent: filtered, step: 1 });

                    }

                }}
                ActionsWrapper={ActionsWrapper}
                contentLoader={() => SKUApi.GetAllContent()}
                ContentWrapper={ContentWrapper}
                onClose={onClose}
                selectedContent={selectedContent}
            />
        );

    }

    return (
        <>
            <ContentWrapper>
                <Container fluid className="details">
                    <Row className="d-flex mb-2">
                        <Col md={6}>
                            <GMetrixInput
                                autoFocus
                                required
                                onChange={e => {

                                    const raw = (e.target.value ?? '').replace(/[^A-Za-z0-9-]/g, '');
                                    setState({ skuName: raw });
                                    if (invalidProps.name.validType) {

                                        setInvalidProps(prev => ({ ...prev, name: { message: '', validType: '' } }));

                                    }

                                }}
                                feedbackMessage={invalidProps.name.message}
                                infoTitle={formatMessage({ id: 'the_sku_may_contain_letters_numbers_and.7180a62d', defaultMessage: 'The SKU may contain letters, numbers, and hyphens.' })}
                                label={formatMessage({ id: 'sku.072d52e0', defaultMessage: 'SKU' })}
                                maxLength={50}
                                onBlur={handleNameBlur}
                                placeholder={formatMessage({ id: 'enter_a_unique_sku.f8eb4294', defaultMessage: 'Enter a unique SKU' })}
                                validType={invalidProps.name.validType || undefined}
                                value={skuName}
                            />
                        </Col>
                        <Col md={6}>
                            <GMetrixInput
                                infoTitle={formatMessage({ id: 'what_should_be_added_to_the_code_identif.0b394957', defaultMessage: 'What should be added to the code Identifier.' })}
                                label={formatMessage({ id: 'additional_identifier.74c49804', defaultMessage: 'Additional Identifier' })}
                                maxLength={250}
                                onChange={e => setState({ additionalIdentifier: e.target.value })}
                                placeholder={formatMessage({ id: 'optional.4ccb9e26', defaultMessage: 'Optional' })}
                                value={additionalIdentifier}
                            />
                        </Col>
                    </Row>
                    <Row className="d-flex mb-2">
                        <Col md={6}>
                            <GMetrixInput
                                required
                                onChange={e => {

                                    const raw = e.target.value;
                                    if (raw === '') {

                                        setState({ numberOfRedemptions: '' });
                                        return;

                                    }
                                    if (!/^\d+$/.test(raw)) return;
                                    setState({ numberOfRedemptions: parseInt(raw, 10) });

                                }}
                                feedbackMessage={invalidProps.redemptions.message}
                                infoTitle={formatMessage({ id: 'how_many_unique_students_can_redeem_the.595dd9df', defaultMessage: 'How many unique students can redeem the code.' })}
                                inputMode="numeric"
                                label={formatMessage({ id: 'number_of_redemptions.387cb28b', defaultMessage: 'Number of Redemptions' })}
                                max={MAX_REDEMPTIONS}
                                min={1}
                                subLabel={`(Max ${MAX_REDEMPTIONS})`}
                                type="number"
                                validType={invalidProps.redemptions.validType || undefined}
                                value={numberOfRedemptions}
                            />
                        </Col>
                    </Row>
                </Container>
            </ContentWrapper>
            <ActionsWrapper>
                <GMetrixButton className="mx-2" onClick={() => setState({ step: 0 })} variant="secondary">
                    <FormattedMessage defaultMessage="Back" id="back.b6238c9e" />
                </GMetrixButton>
                <GMetrixButton disabled={saving || validating} onClick={handleCreate} variant="primary">
                    {saving ? <i className="fa-solid fa-spinner-third fa-spin" /> : <FormattedMessage defaultMessage="Create" id="create.be56b47d" />}
                </GMetrixButton>
            </ActionsWrapper>
        </>
    );

};

NewSKUForm.displayName = 'NewSKUForm';

export default NewSKUForm;
